import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, XCircle, Download, Mail, Loader2 } from 'lucide-react';
import axios from 'axios';
import { trackEvent } from '@/components/Analytics';
import { GoogleReviewPrompt } from '@/components/GoogleReviewPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [order, setOrder] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [actionError, setActionError] = useState('');
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(0);
    }
    // sessionId/orderId are URL-derived and only change on full nav; pollPaymentStatus
    // and fetchReceipt are stable closures defined in this component scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, orderId]);

  const fetchReceipt = async () => {
    if (!orderId || !sessionId) return;
    try {
      const response = await axios.get(
        `${API}/orders/${orderId}/receipt`,
        { params: { session_id: sessionId }, withCredentials: false }
      );
      setOrder(response.data);
      // GA4 purchase event — fires once per session (guarded by sessionStorage key)
      try {
        const key = `ga4_purchase_${response.data.invoice_number || orderId}`;
        if (!sessionStorage.getItem(key)) {
          trackEvent('purchase', {
            transaction_id: response.data.invoice_number || orderId,
            value: Number(response.data.total || 0),
            currency: response.data.currency || 'GBP',
            tax: Number(response.data.vat || 0),
            shipping: 0,
            coupon: response.data.coupon_code || undefined,
            items: [{
              item_id: response.data.product_id || 'biocurcumax',
              item_name: 'BCM-95®',
              price: Number(response.data.total || 0) / Math.max(1, response.data.quantity || 1),
              quantity: response.data.quantity || 1,
            }],
          });
          sessionStorage.setItem(key, '1');
        }
      } catch (e) { /* analytics is best-effort */ }
    } catch (e) {
      // Not paid yet or session_id mismatch — keep retrying via the poller.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('PaymentSuccess.fetchReceipt failed', e);
      }
    }
  };

  const pollPaymentStatus = async (attempt) => {
    if (attempt >= 6) {
      setStatus('timeout');
      return;
    }
    try {
      const response = await axios.get(`${API}/orders/payment-status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setStatus('paid');
        // Mark this device as having purchased so the first-time-buyer
        // popup never fires again (the popup keys off this localStorage
        // flag, not on "seen the popup once").
        try { localStorage.setItem('iiy_has_purchased', '1'); } catch (e) { /* private mode */ }
        fetchReceipt();
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setStatus('processing');
        setTimeout(() => pollPaymentStatus(attempt + 1), 2000);
      }
    } catch (e) {
      setTimeout(() => pollPaymentStatus(attempt + 1), 2000);
    }
  };

  const handleDownload = async () => {
    if (!orderId || !sessionId || downloading) return;
    setActionError('');
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API}/orders/${orderId}/invoice/download`,
        {
          params: { session_id: sessionId },
          responseType: 'blob',
          withCredentials: false,
        }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice-${order?.invoice_number || orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setActionError('Sorry, the invoice could not be downloaded. Please try again or contact support@iiypv.com.');
    } finally {
      setDownloading(false);
    }
  };

  const handleEmail = async () => {
    if (!orderId || !sessionId || emailing) return;
    setActionError('');
    setEmailing(true);
    try {
      const response = await axios.post(
        `${API}/orders/${orderId}/invoice/email`,
        { session_id: sessionId },
        { withCredentials: false }
      );
      if (response.data?.ok) {
        setEmailSent(true);
      }
    } catch (e) {
      setActionError(
        e?.response?.data?.detail ||
        'Sorry, we could not send the email. Please try again or contact support@iiypv.com.'
      );
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="min-h-screen py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center mb-12">
          {status === 'paid' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 font-heading" data-testid="payment-success-title">Payment Successful!</h1>
              <p className="text-lg text-muted-foreground">Your order has been confirmed. A copy of your VAT invoice is on its way to your inbox.</p>
            </>
          )}
          {(status === 'checking' || status === 'processing') && (
            <>
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-blue-600 animate-pulse" />
              </div>
              <h1 className="text-4xl font-bold mb-4 font-heading">Processing Payment...</h1>
              <p className="text-lg text-muted-foreground">Please wait while we confirm your payment.</p>
            </>
          )}
          {(status === 'expired' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-4xl font-bold mb-4 font-heading">Payment Issue</h1>
              <p className="text-lg text-muted-foreground">There was an issue with your payment. Please contact us at support@iiypv.com</p>
            </>
          )}
        </motion.div>

        {status === 'paid' && order && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-3xl p-8 shadow-soft mb-8" data-testid="payment-receipt-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold font-heading">Your Receipt</h2>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-green-100 text-green-700">
                <span className="w-1.5 h-1.5 rounded-full bg-green-600" /> PAID
              </span>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-border"><span className="text-muted-foreground">Invoice Number</span><span className="font-mono font-medium" data-testid="receipt-invoice-number">{order.invoice_number}</span></div>
              <div className="flex justify-between py-2 border-b border-border"><span className="text-muted-foreground">Product</span><span className="font-medium">{order.product_name}</span></div>
              <div className="flex justify-between py-2 border-b border-border"><span className="text-muted-foreground">Quantity</span><span className="font-medium">{order.quantity} {order.quantity === 1 ? 'bottle' : 'bottles'}</span></div>
              <div className="flex justify-between py-2 border-b border-border"><span className="text-muted-foreground">Price per Bottle</span><span>£{order.price_per_bottle?.toFixed(2)}</span></div>
              {order.discount_amount > 0 && <div className="flex justify-between py-2 border-b border-border text-green-600"><span>Discount ({order.discount_pct}%)</span><span>-£{order.discount_amount?.toFixed(2)}</span></div>}
              {order.is_vat_business && (
                <>
                  <div className="flex justify-between py-2 border-b border-border text-muted-foreground"><span>Net (ex-VAT)</span><span>£{order.subtotal?.toFixed(2)}</span></div>
                  <div className="flex justify-between py-2 border-b border-border text-muted-foreground"><span>VAT (20%)</span><span>£{order.vat_amount?.toFixed(2)}</span></div>
                </>
              )}
              <div className="flex justify-between py-3 font-bold text-lg"><span>Total Paid</span><span data-testid="receipt-total">£{order.total?.toFixed(2)}</span></div>
              {!order.is_vat_business && (
                <p className="text-[11px] text-muted-foreground leading-snug">All-inclusive of UK VAT (20%) and Royal Mail 48 delivery.</p>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-border space-y-1">
              <p className="text-sm"><span className="text-muted-foreground">Delivery:</span> <span className="font-medium">{order.delivery_option_name}</span> ({order.delivery_timeline})</p>
              {order.paid_at && (
                <p className="text-xs text-muted-foreground">Payment confirmed at {new Date(order.paid_at).toLocaleString('en-GB', { dateStyle: 'long', timeStyle: 'short' })} UTC</p>
              )}
            </div>
          </motion.div>
        )}

        {status === 'paid' && order && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-gradient-to-br from-primary/5 to-primary/0 border border-primary/20 rounded-3xl p-6 md:p-8 mb-8">
            <h3 className="text-lg font-bold mb-2 font-heading">Your invoice is ready</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Download a copy now, or have it sent to <span className="font-medium text-foreground">{order.delivery_address?.email}</span>.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                onClick={handleDownload}
                disabled={downloading}
                data-testid="download-invoice-btn"
                className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 px-5 rounded-xl transition-all duration-300"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {downloading ? 'Preparing PDF…' : 'Download Invoice (PDF)'}
              </button>
              <button
                onClick={handleEmail}
                disabled={emailing || emailSent}
                data-testid="email-invoice-btn"
                className="inline-flex items-center justify-center gap-2 bg-white border-2 border-primary text-primary hover:bg-primary/5 disabled:opacity-60 disabled:cursor-not-allowed font-semibold py-3 px-5 rounded-xl transition-all duration-300"
              >
                {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {emailSent ? 'Email sent ✓' : emailing ? 'Sending…' : 'Send to Email'}
              </button>
            </div>
            {emailSent && (
              <p className="text-xs text-green-700 mt-3" data-testid="email-invoice-success">
                Sent to {order.delivery_address?.email}. Check your inbox (and spam folder).
              </p>
            )}
            {actionError && (
              <p className="text-xs text-red-600 mt-3" data-testid="invoice-action-error">{actionError}</p>
            )}
          </motion.div>
        )}

        {status === 'paid' && <GoogleReviewPrompt />}

        <div className="text-center">
          <button onClick={() => navigate('/')} className="bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300" data-testid="back-home-button">Back to Home</button>
        </div>
      </div>
    </div>
  );
}
